/**
 * Centralized route generation — single source of truth for all in-app URLs.
 *
 * Every component that navigates MUST import from here.
 * Never construct wiki or entity URLs inline.
 */

/** Navigate to a wiki article by slug. */
export function wikiArticleRoute(slug: string): string {
	return `/wiki?article=${encodeURIComponent(slug)}`;
}

/** Navigate to wiki with a search query pre-filled. */
export function wikiSearchRoute(query: string): string {
	return `/wiki?search=${encodeURIComponent(query)}`;
}

/** Wiki homepage. */
export const WIKI_HOME = "/wiki";

const q = encodeURIComponent;

/** A project, open in the projects page's inspector. */
export function projectRoute(slug: string): string {
	return `/projects?project=${q(slug)}`;
}

/** A goal, open on the goals page. */
export function goalRoute(slug: string): string {
	return `/goals?goal=${q(slug)}`;
}

/** A task's URL value: tasks are identified by project AND slug. */
export function taskUrlId(project: string, slug: string): string {
	return `${project}/${slug}`;
}

/** Inverse of `taskUrlId`. A bare slug (older links) has no project. */
export function parseTaskUrlId(value: string): { project: string; slug: string } {
	const at = value.lastIndexOf("/");
	return at < 0 ? { project: "", slug: value } : { project: value.slice(0, at), slug: value.slice(at + 1) };
}

/** A task, open in the tasks page's inspector. */
export function taskRoute(project: string, slug: string): string {
	return `/tasks?task=${q(taskUrlId(project, slug))}`;
}

/** A contact card, open in the contacts page's inspector. */
export function contactRoute(slug: string): string {
	return `/contacts?contact=${q(slug)}`;
}

/** An agent, open in the agents editor. */
export function agentRoute(key: string): string {
	return `/agents?view=editor&agent=${q(key)}`;
}

/** A knowledge base, selected in the wiki. */
export function kbRoute(slug: string): string {
	return `/wiki?kb=${q(slug)}`;
}

/** A conversation. */
export function chatRoute(conversationId: string, messageId?: string): string {
	const base = `/chat/${q(conversationId)}`;
	return messageId ? `${base}?msg=${q(messageId)}` : base;
}

/** Build `path?k=v&…`, dropping empty values, in the given key order. */
export function withQuery(path: string, params: Record<string, string | number | null | undefined>): string {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
	}
	const qs = search.toString();
	return qs ? `${path}?${qs}` : path;
}

/** A journal day (YYYY-MM-DD), optionally on one of its tabs. */
export function journalRoute(date?: string, tab?: string): string {
	return withQuery("/journal", { date, tab });
}

/** The memory page on a layer (daily/week/…/dreams), optionally a date. */
export function memoryRoute(layer?: string, date?: string): string {
	return withQuery("/memory", { layer, date });
}

/** The calendar on a view (month/week/day/agenda) around a date. */
export function calendarRoute(view?: string, date?: string): string {
	return withQuery("/calendar", { view, date });
}

/** The finance ledger with filters (`status`, `unattributed`, `project`, `q`, `focus`, …). */
export function ledgerRoute(filters: Record<string, string | null | undefined> = {}): string {
	return withQuery("/finance/ledger", filters);
}

/** The decision log, filtered (e.g. to one correlation chain). */
export function decisionsRoute(filters: { correlation_id?: string; actor?: string; action_type?: string } = {}): string {
	return withQuery("/decisions", filters);
}

/** One proposal, open in the proposals inbox's inspector. */
export function proposalRoute(id: string): string {
	return withQuery("/proposals", { proposal: id });
}

/** A settings section by its anchor id (e.g. "settings-remote-access").
 *  The page maps the section to its tab and scrolls to it. */
export function settingsRoute(sectionId?: string): string {
	return sectionId ? `/settings#${sectionId}` : "/settings";
}

/**
 * The record a LOGICAL vault path names, and its page — or null for a path
 * that is not a record file. Graph nodes for vault records are keyed by this
 * path (`goals/x.md`, `projects/p/tasks/t.md`, `system/agents/k.md`), so the
 * graph opens THAT record; it used to open the bare module, which auto-opens
 * the most recent item instead (2026-10-02). `type` is the graph entity type
 * the file represents, so a caller can refuse a mismatched match.
 */
export function vaultRecord(path: string): { type: string; route: string } | null {
	const parts = path.split("/");
	const [root, a, b, c, d] = parts;
	if (root === "projects" && a && parts.length === 3 && b === "PROJECT.md") {
		return { type: "project", route: projectRoute(a) };
	}
	if (root === "projects" && a && b === "tasks" && c?.endsWith(".md") && parts.length === 4) {
		return { type: "task", route: taskRoute(a, c.slice(0, -3)) };
	}
	if (root === "goals" && a?.endsWith(".md") && parts.length === 2) {
		return { type: "goal", route: goalRoute(a.slice(0, -3)) };
	}
	if (root === "system" && a === "agents" && b?.endsWith(".md") && parts.length === 3) {
		return { type: "ai_agent", route: agentRoute(b.slice(0, -3)) };
	}
	if (root === "system" && a === "skills" && c && d === "SKILL.md" && parts.length === 5) {
		return { type: "skill", route: `/skills?skill=${q(c)}` };
	}
	return null;
}

/**
 * The page for one record of a graph/entity type, carrying its id — never
 * the bare module. A bare `/projects` or `/goals` does not show "nothing": it
 * opens the most recently edited item, so an id-less link silently opens the
 * WRONG record (the bug this replaces, 2026-10-02). Returns null for a type
 * with no page of its own, so the caller can fall back to the wiki honestly.
 */
export function recordRoute(type: string, id: string): string | null {
	switch (type) {
		case "project": return projectRoute(id);
		case "goal": return goalRoute(id);
		case "task": {
			const { project, slug } = parseTaskUrlId(id);
			return taskRoute(project, slug);
		}
		case "agent":
		case "ai_agent": return agentRoute(id);
		case "kb": return kbRoute(id);
		case "person":
		case "contact": return contactRoute(id);
		case "skill": return `/skills?skill=${q(id)}`;
		default: return null;
	}
}

/**
 * An in-app path that is safe to navigate to after a detour (`?next=`), or
 * null. Only a same-origin absolute path qualifies: `//evil.example` and
 * `/\\evil.example` are protocol-relative to a browser and would leave the app.
 */
export function safeInAppPath(raw: string | null | undefined): string | null {
	if (!raw || !raw.startsWith("/")) return null;
	if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
	if ([...raw].some((ch) => ch.charCodeAt(0) < 0x20)) return null; // control chars: header/log injection
	return raw;
}

/** First-run onboarding, remembering where the person was going. */
export function onboardingRoute(next?: string): string {
	const target = safeInAppPath(next);
	if (!target || target === "/dashboard" || target.startsWith("/onboarding")) return "/onboarding";
	return `/onboarding?next=${encodeURIComponent(target)}`;
}
